import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../Layout";
import { listBaskets, getCompetitorData, getTrackingImageProxyUrl, fetchProductMedia, getStoredProductMediaBulk, getMediaChangeSummary, markChangeReviewed } from "../../services/trackingService";
import { saveSnapshot, loadSnapshot, clearSnapshot } from "../../utils/mediaHistory";
import { diffMediaSnapshot, fieldDisplayConfig, summarizeChanges } from "../../utils/mediaChangeDetector";
import "../../assets/css/tracking.css";
import "bootstrap/dist/css/bootstrap.min.css";

// Inline styles for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7); }
    50% { box-shadow: 0 0 0 10px rgba(255, 193, 7, 0); }
  }
  .pulse { animation: pulse 2s infinite; }
`;
document.head.appendChild(styleSheet);

const fmtPrice = (p) => p != null ? `$${Number(p).toFixed(2)}` : '—';
const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '—';

const fmtRelative = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
};

const VideoThumbnail = ({ src, alt }) => {
  const proxied = getTrackingImageProxyUrl(src);
  const [imgSrc, setImgSrc] = useState(proxied);
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  return (
    <img
      src={imgSrc}
      alt={alt}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => {
        if (imgSrc === proxied) setImgSrc(src);
        else setFailed(true);
      }}
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  );
};

const InfographicTile = ({ url, index }) => {
  const proxied = getTrackingImageProxyUrl(url);
  const [src, setSrc] = useState(proxied);
  const [failed, setFailed] = useState(false);

  const handleError = () => {
    if (src === proxied) {
      setSrc(url); // fall back to direct URL (CORS may still block, but worth trying)
    } else {
      setFailed(true);
    }
  };

  return (
    <div className="col-6 col-lg-4">
      <div
        className="rounded border overflow-hidden bg-light d-flex align-items-center justify-content-center"
        style={{ height: '180px', cursor: 'pointer' }}
        onClick={() => window.open(proxied, '_blank', 'noopener,noreferrer')}
      >
        {failed ? (
          <div className="text-center text-muted small p-2">
            <i className="bi bi-image-fill d-block fs-3"></i>
            <a href={proxied} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              Open image
            </a>
          </div>
        ) : (
          <img
            src={src}
            alt={`Infographic ${index + 1}`}
            style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain' }}
            onError={handleError}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        )}
      </div>
      <div className="text-center text-muted mt-1" style={{ fontSize: '0.75rem' }}>
        Image {index + 1}
      </div>
    </div>
  );
};

const EmptyMediaState = ({ onTrack, isLoading }) => (
  <div className="text-center py-5 px-3">
    <i className="bi bi-cloud-arrow-down display-4 text-muted d-block mb-2"></i>
    <h6 className="fw-bold mb-2">No media loaded yet</h6>
    <p className="text-muted small mb-3">
      Track this product to fetch infographics, A+ content, UGC videos and review insights from Amazon.
    </p>
    {onTrack && (
      <button className="btn btn-success btn-sm" onClick={onTrack} disabled={isLoading}>
        {isLoading
          ? <><span className="spinner-border spinner-border-sm me-1" /> Fetching media...</>
          : <><i className="bi bi-cloud-download me-1"></i> Track Product to Load Media</>}
      </button>
    )}
  </div>
);

const ProductModal = ({ row, media, imgSrc, onClose, onTrack, isTracked, isLoadingTrack }) => {
  const [activeTab, setActiveTab] = useState('details');

  // When media changes (e.g., just finished tracking), force re-render by staying on details tab
  useEffect(() => {
    setActiveTab('details');
  }, [media?._persisted?.last_updated_at]);

  const hasMedia = !!media;
  const infographics = media?.infographics || [];
  const ugcVideos = media?.ugc_videos || [];
  const reviewTopics = media?.review_topics || [];
  const aplusContent = media?.aplus_content || [];
  const aplusType = media?.aplus_content_type;
  const bulletPoints = media?.bullet_points || [];
  const description = media?.description || row.product_description || null;
  const reviewsSummaryText = media?.reviews_summary_text;
  const badges = media?.badges || [];
  const tags = media?.tags || [];

  const tabs = [
    { id: 'details', label: 'Product Details', icon: 'bi-info-circle' },
    { id: 'infographics', label: `Infographics${infographics.length ? ` (${infographics.length})` : ''}`, icon: 'bi-image' },
    { id: 'aplus', label: 'A+ Content', icon: 'bi-plus-square' },
    { id: 'ugc', label: `UGC Videos${ugcVideos.length ? ` (${ugcVideos.length})` : ''}`, icon: 'bi-play-circle' },
    { id: 'reviews', label: 'Review Insights', icon: 'bi-chat-left-text' },
  ];

  const renderEmpty = (label) => (
    <EmptyMediaState
      onTrack={!isTracked && onTrack ? () => onTrack(row) : null}
      isLoading={isLoadingTrack}
    />
  );

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1050 }} onClick={onClose}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content" style={{ maxHeight: '90vh' }}>
          {/* Header */}
          <div className="modal-header border-bottom pb-3">
            <div>
              <h5 className="modal-title fw-bold mb-1">{row.product_title || media?.product_title || 'Product Details'}</h5>
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {row.brand && <span className="badge bg-secondary">{row.brand}</span>}
                <span className="badge border text-primary font-monospace">{row.asin}</span>
                {row.sub_category_name && <span className="badge bg-light text-muted">{row.sub_category_name}</span>}
                {badges.map((b, i) => <span key={i} className="badge bg-warning text-dark">{b}</span>)}
                {tags.map((t, i) => <span key={i} className="badge bg-info text-dark">{t}</span>)}
                {hasMedia && media.last_updated_at && (
                  <span className="badge bg-success bg-opacity-10 text-white border border-success border-opacity-25" title={fmtDateTime(media.last_updated_at)}>
                    <i className="bi bi-clock-history me-1"></i>Tracked {fmtRelative(media.last_updated_at)}
                  </span>
                )}
                {!hasMedia && <span className="badge bg-light text-muted border"><i className="bi bi-exclamation-circle me-1"></i>Not yet tracked</span>}
              </div>
            </div>
            <button type="button" className="btn-close ms-3" onClick={onClose}></button>
          </div>

          <div className="modal-body p-0" style={{ overflowY: 'auto' }}>
            <div className="row g-0" style={{ minHeight: '500px' }}>
              {/* Left: Hero Image + quick stats */}
              <div className="col-md-3 border-end p-3 d-flex flex-column align-items-center bg-light">
                <div className="w-100 mb-3 rounded overflow-hidden bg-white d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                  {imgSrc ? (
                    <img src={imgSrc} alt={row.asin} style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                  ) : (
                    <i className="bi bi-box-seam fs-1 text-muted"></i>
                  )}
                </div>

                <div className="w-100 small">
                  {(row.product_price || row.product_original_price) && (
                    <div className="mb-2 p-2 bg-white rounded border">
                      {row.product_price && <div className="fw-bold text-success fs-6">{fmtPrice(row.product_price)}</div>}
                      {row.product_original_price && Number(row.product_original_price) > Number(row.product_price) && (
                        <div className="text-decoration-line-through text-muted">{fmtPrice(row.product_original_price)}</div>
                      )}
                      {row.discount && <div className="badge bg-danger mt-1">-{Number(row.discount)}%</div>}
                    </div>
                  )}
                  {row.product_star_rating && (
                    <div className="mb-1 d-flex align-items-center gap-1">
                      <span>⭐ <strong>{row.product_star_rating}</strong></span>
                      <span className="text-muted">({fmtNum(row.product_num_ratings)} reviews)</span>
                    </div>
                  )}
                  {row.sales_volume && <div className="mb-1"><strong>Sales:</strong> {row.sales_volume}</div>}
                  {row.rank_value && <div className="mb-1"><strong>Rank:</strong> #{fmtNum(row.rank_value)}</div>}
                </div>

                {(row.product_url || media?.product_url) && (
                  <a href={media?.product_url || row.product_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm w-100 mt-2">
                    <i className="bi bi-box-arrow-up-right me-1"></i> View on Amazon
                  </a>
                )}

                <button
                  className={`btn btn-sm w-100 mt-2 ${isTracked ? 'btn-outline-danger' : 'btn-success'}`}
                  onClick={() => onTrack?.(row)}
                  disabled={isLoadingTrack}
                >
                  {isLoadingTrack
                    ? <><span className="spinner-border spinner-border-sm me-1" /> Fetching...</>
                    : <><i className={`bi ${isTracked ? 'bi-arrow-clockwise' : 'bi-cloud-download'} me-1`}></i>{isTracked ? 'Refresh / Untrack' : 'Track Product'}</>}
                </button>
              </div>

              {/* Right: Tabs */}
              <div className="col-md-9 d-flex flex-column">
                <div className="border-bottom px-3 pt-2" style={{ overflowX: 'auto', overflowY: 'hidden', scrollBehavior: 'smooth' }}>
                  <ul className="nav nav-tabs border-0 gap-1 flex-nowrap" style={{ minWidth: 'min-content' }}>
                    {tabs.map(t => (
                      <li key={t.id} className="nav-item">
                        <button
                          className={`nav-link px-3 py-2 ${activeTab === t.id ? 'active fw-semibold' : 'text-muted'}`}
                          style={{ fontSize: '0.85rem', border: 'none', borderBottom: activeTab === t.id ? '2px solid #0d6efd' : '2px solid transparent', whiteSpace: 'nowrap' }}
                          onClick={() => setActiveTab(t.id)}
                        >
                          <i className={`bi ${t.icon} me-1`}></i>{t.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 flex-grow-1" style={{ overflowY: 'auto' }}>

                  {/* Product Details */}
                  {activeTab === 'details' && (
                    <div>
                      {bulletPoints.length > 0 && (
                        <>
                          <h6 className="fw-bold mb-3">About this item</h6>
                          <ul className="text-start" style={{ lineHeight: '1.8', paddingLeft: '1.5rem', listStyleType: 'disc', margin: 0 }}>
                            {bulletPoints.map((b, i) => (
                              <li key={i} className="mb-2 text-dark" style={{ fontSize: '0.95rem' }}>
                                {b}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {description && !bulletPoints.length && (
                        <>
                          <h6 className="fw-bold mb-3">Product Description</h6>
                          <p className="text-muted" style={{ lineHeight: '1.7' }}>{description}</p>
                        </>
                      )}
                      {!bulletPoints.length && !description && !hasMedia && renderEmpty()}

                      <hr />
                      <h6 className="fw-bold mb-3">Specifications</h6>
                      <div className="row g-2 small">
                        {[
                          ['Brand', row.brand || media?.brand],
                          ['ASIN', row.asin],
                          ['Category', row.sub_category_name || media?.sub_category_name],
                          ['Price', row.product_price ? fmtPrice(row.product_price) : null],
                          ['Rating', row.product_star_rating ? `${row.product_star_rating} / 5` : null],
                          ['Reviews', row.product_num_ratings ? fmtNum(row.product_num_ratings) : null],
                          ['Sales Volume', row.sales_volume],
                          ['Rank', row.rank_value ? `#${fmtNum(row.rank_value)}` : null],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <div key={label} className="col-6">
                            <div className="bg-light rounded p-2">
                              <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
                              <div className="fw-semibold">{value}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Infographics */}
                  {activeTab === 'infographics' && (
                    <div>
                      <h6 className="fw-bold mb-3">Infographics</h6>
                      {infographics.length === 0 ? renderEmpty() : (
                        <div className="row g-3">
                          {infographics.map((img, i) => {
                            const url = typeof img === 'string' ? img : (img?.link || img?.image);
                            if (!url) return null;
                            return (
                              <InfographicTile key={`${url}-${i}`} url={url} index={i} />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* A+ Content */}
                  {activeTab === 'aplus' && (
                    <div>
                      <h6 className="fw-bold mb-3">A+ Content</h6>
                      {(!aplusContent || (Array.isArray(aplusContent) && aplusContent.length === 0)) ? renderEmpty() : (
                        <>
                          {aplusType && (
                            <div className="mb-3 p-3 bg-light rounded border-start border-primary border-3">
                              <div className="text-muted small mb-1">Content Type</div>
                              <div className="fw-semibold">{aplusType}</div>
                            </div>
                          )}
                          <div className="d-flex flex-column gap-3">
                            {(Array.isArray(aplusContent) ? aplusContent : [{ text: aplusContent }]).map((module, i) => (
                              <div key={i} className="border rounded p-3 bg-white">
                                {module.title && <h6 className="fw-bold mb-2">{module.title}</h6>}
                                {module.image && (
                                  <img
                                    src={getTrackingImageProxyUrl(module.image)}
                                    alt={module.title || `Module ${i + 1}`}
                                    className="img-fluid rounded mb-2"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                {module.text && <p className="text-muted small mb-2">{module.text}</p>}
                                {Array.isArray(module.features) && (
                                  <div className="row g-2 mt-2">
                                    {module.features.map((f, j) => (
                                      <div key={j} className="col-md-4">
                                        <div className="border rounded p-2 h-100">
                                          {f.image && (
                                            <img
                                              src={getTrackingImageProxyUrl(f.image)}
                                              alt={f.title}
                                              className="img-fluid rounded mb-2"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                          )}
                                          {f.title && <div className="fw-semibold small">{f.title}</div>}
                                          {f.text && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{f.text}</div>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* UGC Videos */}
                  {activeTab === 'ugc' && (
                    <div>
                      <h6 className="fw-bold mb-3">UGC Videos</h6>
                      {ugcVideos.length === 0 ? renderEmpty() : (
                        <div className="row g-3">
                          {ugcVideos.map((video, i) => (
                            <div key={i} className="col-md-6">
                              <div className="border rounded overflow-hidden bg-white h-100 d-flex flex-column">
                                <a href={video.url} target="_blank" rel="noopener noreferrer" className="position-relative d-block bg-dark text-decoration-none" style={{ height: '160px' }}>
                                  {video.thumbnail ? (
                                    <VideoThumbnail src={video.thumbnail} alt={video.title} />
                                  ) : null}
                                  <div className="position-absolute top-50 start-50 translate-middle">
                                    <i className="bi bi-play-circle-fill text-white" style={{ fontSize: '3rem', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' }}></i>
                                  </div>
                                  {video.duration && (
                                    <span className="position-absolute bottom-0 end-0 m-2 badge bg-dark">{video.duration}</span>
                                  )}
                                </a>
                                <div className="p-2 flex-grow-1 d-flex flex-column">
                                  <div className="fw-semibold small mb-1" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {video.title || `Video ${i + 1}`}
                                  </div>
                                  <div className="text-muted mt-auto d-flex justify-content-between" style={{ fontSize: '0.7rem' }}>
                                    {video.vendor && <span><i className="bi bi-person me-1"></i>{video.vendor}</span>}
                                    {video.date && <span><i className="bi bi-calendar me-1"></i>{video.date}</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Review Insights */}
                  {activeTab === 'reviews' && (
                    <div>
                      <h6 className="fw-bold mb-3">Review Insights</h6>

                      {row.product_star_rating && (
                        <div className="mb-3 p-3 bg-light rounded d-flex align-items-center gap-4">
                          <div className="text-center">
                            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', lineHeight: 1 }}>{row.product_star_rating}</div>
                            <div className="text-warning">{'★'.repeat(Math.round(row.product_star_rating))}{'☆'.repeat(5 - Math.round(row.product_star_rating))}</div>
                            <div className="text-muted small">{fmtNum(row.product_num_ratings)} reviews</div>
                          </div>
                          {(() => {
                            const custReviews = media?.reviews_summary?.customer_reviews || media?.reviews_summary;
                            const hasCustReviews = custReviews && typeof custReviews === 'object' && Object.keys(custReviews).some(k => k.includes('star'));
                            return hasCustReviews ? (
                              <div className="flex-grow-1 small">
                                {[5, 4, 3, 2, 1].map(star => {
                                  const pct = custReviews[`${star} star`] || 0;
                                  return (
                                    <div key={star} className="d-flex align-items-center gap-2 mb-1">
                                      <span style={{ width: '12px' }}>{star}</span>
                                      <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                        <div className="progress-bar bg-warning" style={{ width: `${pct}%` }}></div>
                                      </div>
                                      <span className="text-muted" style={{ width: '32px' }}>{pct}%</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}

                      {reviewsSummaryText && (
                        <div className="mb-3 p-3 border rounded bg-white">
                          <h6 className="text-muted small fw-bold mb-2">SUMMARY</h6>
                          <p className="text-muted small mb-0">{reviewsSummaryText}</p>
                        </div>
                      )}

                      {reviewTopics.length === 0 ? renderEmpty() : (
                        <>
                          <h6 className="text-muted small fw-bold mb-3">REVIEW TOPICS</h6>
                          <div className="d-flex flex-column gap-2">
                            {reviewTopics.map((topic, i) => {
                              const sentimentColor = topic.sentiment === 'positive' ? 'success'
                                : topic.sentiment === 'negative' ? 'danger'
                                : 'warning';
                              return (
                                <div key={i} className="border rounded p-2 bg-white">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="fw-semibold small">{topic.title}</span>
                                    <span className={`badge bg-${sentimentColor} bg-opacity-25 text-white`}>{topic.sentiment}</span>
                                  </div>
                                  {topic.mentions && (
                                    <div className="d-flex gap-3 small text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                                      <span>Total: <strong>{fmtNum(topic.mentions.total)}</strong></span>
                                      <span className="text-success">+ {fmtNum(topic.mentions.positive)}</span>
                                      <span className="text-danger">− {fmtNum(topic.mentions.negative)}</span>
                                    </div>
                                  )}
                                  {topic.summary && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{topic.summary}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const ChangesModal = ({ changedFields, productTitle, asin, onClose, onMarkReviewed }) => {
  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1100 }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header bg-warning text-dark border-0">
            <h5 className="modal-title fw-bold">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>Content Changes Detected
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-warning mb-3">
              <strong>{productTitle}</strong> has been updated on Amazon
            </div>
            <h6 className="fw-bold mb-3">Updated Content:</h6>
            <div className="d-flex flex-column gap-2">
              {changedFields.map((field) => {
                const config = fieldDisplayConfig[field.field] || { label: field.label, icon: 'bi-info-circle' };
                return (
                  <div key={field.field} className="border rounded p-3 bg-light">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className={`bi ${config.icon} fs-5 text-warning`}></i>
                      <h6 className="mb-0">{config.label}</h6>
                      {field.delta && <span className="badge bg-warning text-dark ms-auto">{field.delta}</span>}
                    </div>
                    <div className="small text-muted">
                      {field.type === 'count' && (
                        <span>Before: <strong>{field.before}</strong> → Now: <strong>{field.after}</strong></span>
                      )}
                      {field.type === 'content' && (
                        <span className="fst-italic">Content was edited</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="modal-footer border-top">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn btn-warning text-dark fw-bold" onClick={() => {
              onMarkReviewed?.(asin);
              onClose();
            }}>
              <i className="bi bi-check-circle me-1"></i> Mark as Reviewed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ row, media, onTrack, isTracked, isLoadingTrack, changedFields = [], onMarkReviewed }) => {
  const [imgSrc, setImgSrc] = useState(row.product_photo ? getTrackingImageProxyUrl(row.product_photo) : '');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setImgSrc(row.product_photo ? getTrackingImageProxyUrl(row.product_photo) : '');
  }, [row.product_photo]);
  const [showChangesModal, setShowChangesModal] = useState(false);

  const mediaCount = media ? (media.infographics?.length || 0) + (media.ugc_videos?.length || 0) : 0;

  return (
    <>
      <div
        className="card h-100 shadow-sm position-relative"
        style={{ transition: 'all 0.2s ease' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
        }}
      >
        {isTracked && (
          <>
            <span
              className="position-absolute top-0 end-0 m-2 badge bg-success"
              style={{ zIndex: 2 }}
              title={media?.last_updated_at ? `Last tracked: ${fmtDateTime(media.last_updated_at)}` : 'Tracked'}
            >
              <i className="bi bi-check-circle-fill me-1"></i>
              {media?.last_updated_at ? fmtRelative(media.last_updated_at) : 'Tracked'}
            </span>
            {changedFields && changedFields.length > 0 && (
              <button
                className="position-absolute top-0 start-0 m-2 btn btn-sm btn-warning text-dark fw-bold"
                style={{ zIndex: 2, padding: '0.5rem 0.75rem', animation: 'pulse 2s infinite' }}
                onClick={(e) => { e.stopPropagation(); setShowChangesModal(true); }}
                title={`Click to view changes: ${changedFields.map(f => f.label).join(', ')}`}
              >
                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                {changedFields.length} changed
              </button>
            )}
          </>
        )}

        <div
          className="bg-light d-flex align-items-center justify-content-center"
          style={{ height: '180px', overflow: 'hidden', cursor: 'zoom-in' }}
          onClick={() => setShowModal(true)}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={row.asin}
              style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'contain', transition: 'transform 0.3s ease' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onError={() => {
                if (imgSrc !== row.product_photo) setImgSrc(row.product_photo || '');
                else setImgSrc('');
              }}
            />
          ) : (
            <i className="bi bi-box-seam fs-1 text-muted"></i>
          )}
        </div>

        <div className="card-body p-3 d-flex flex-column">
          <div className="mb-1">
            <small className="fw-bold text-muted">{row.brand}</small>
            <small className="text-muted mx-1">—</small>
            <small className="text-primary font-monospace">{row.asin}</small>
          </div>
          <h6 className="card-title mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={row.product_title}>
            {row.product_title || 'No title available'}
          </h6>
          {row.product_price && (
            <div className="mt-2 fw-bold text-success small">${Number(row.product_price).toFixed(2)}</div>
          )}
          {row.product_star_rating && (
            <div className="text-warning small">{'★'.repeat(Math.round(row.product_star_rating))} <span className="text-muted">{row.product_star_rating}</span></div>
          )}

          {isTracked && mediaCount > 0 && (
            <div className="d-flex gap-2 mt-2 small text-muted">
              {media.infographics?.length > 0 && <span><i className="bi bi-image me-1"></i>{media.infographics.length}</span>}
              {media.ugc_videos?.length > 0 && <span><i className="bi bi-play-circle me-1"></i>{media.ugc_videos.length}</span>}
              {media.review_topics?.length > 0 && <span><i className="bi bi-chat-left-text me-1"></i>{media.review_topics.length}</span>}
            </div>
          )}

          <div className="mt-auto pt-3 d-grid gap-2">
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowModal(true)}>
              <i className="bi bi-search me-1"></i> View Details
            </button>
            <button
              className={`btn btn-sm ${isTracked ? 'btn-outline-secondary' : 'btn-success'}`}
              onClick={() => onTrack?.(row)}
              disabled={isLoadingTrack}
            >
              {isLoadingTrack
                ? <><span className="spinner-border spinner-border-sm me-1" /> Fetching media...</>
                : isTracked
                  ? <><i className="bi bi-arrow-clockwise me-1"></i>Refresh / Untrack</>
                  : <><i className="bi bi-cloud-download me-1"></i>Track Product</>}
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <ProductModal
          row={row}
          media={media}
          imgSrc={imgSrc}
          onClose={() => setShowModal(false)}
          onTrack={onTrack}
          isTracked={isTracked}
          isLoadingTrack={isLoadingTrack}
        />
      )}

      {showChangesModal && (
        <ChangesModal
          changedFields={changedFields}
          productTitle={row.product_title}
          asin={row.asin}
          onClose={() => setShowChangesModal(false)}
          onMarkReviewed={onMarkReviewed}
        />
      )}
    </>
  );
};

const MediaTracking = () => {
  const navigate = useNavigate();
  const [baskets, setBaskets] = useState([]);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [mediaData, setMediaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Map<asin, mediaView> — populated when user clicks Track
  const [mediaByAsin, setMediaByAsin] = useState({});
  const [mediaLoadingAsin, setMediaLoadingAsin] = useState(null);
  // Map<asin, DiffResult>
  const [changesByAsin, setChangesByAsin] = useState({});
  const [unreviewedCount, setUnreviewedCount] = useState(0);

  const accountId = localStorage.getItem("selectedAccountValue") || "";

  const loadBaskets = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await listBaskets(accountId);
      setBaskets(res.data || []);
    } catch (err) {
      console.error("Error loading baskets:", err);
    }
  }, [accountId]);

  useEffect(() => { loadBaskets(); }, [loadBaskets]);

  useEffect(() => {
    if (!accountId) return;
    getMediaChangeSummary(accountId, 30)
      .then(data => setUnreviewedCount(data?.unreviewed || 0))
      .catch(() => {});
  }, [accountId]);

  const loadData = useCallback(async () => {
    if (!selectedBasket || !accountId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getCompetitorData(selectedBasket.id, accountId);
      setMediaData(res.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          "Error loading media tracking data"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedBasket, accountId]);

  useEffect(() => {
    setMediaByAsin({});
    setChangesByAsin({});
  }, [selectedBasket]);

  useEffect(() => { loadData(); }, [loadData]);

  // When mediaData arrives, look up stored media for all unique ASINs at once.
  useEffect(() => {
    if (!mediaData?.length) return;
    const uniqueAsins = Array.from(new Set(mediaData.map(m => m.asin).filter(Boolean)));
    if (!uniqueAsins.length) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await getStoredProductMediaBulk(uniqueAsins);
        if (cancelled) return;
        const data = res?.data || {};
        if (Object.keys(data).length) {
          setMediaByAsin(prev => ({ ...data, ...prev })); // prev wins (don't overwrite freshly-fetched media)

          // Now diff each against snapshot
          const newChanges = {};
          for (const [asin, media] of Object.entries(data)) {
            const snapshot = loadSnapshot(accountId, asin);
            if (snapshot) {
              const diff = diffMediaSnapshot(snapshot, media);
              if (diff && diff.hasChanges) {
                newChanges[asin] = diff;
              }
            } else {
              // First save, establish baseline, no alert
              saveSnapshot(accountId, asin, media);
            }
          }
          if (Object.keys(newChanges).length > 0) {
            setChangesByAsin(newChanges);
          }
        }
      } catch (err) {
        console.warn('[MediaTracking] Could not preload stored media:', err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [mediaData, accountId]);

  const handleTrackProduct = async (product) => {
    const isAlreadyTracked = !!mediaByAsin[product.asin];

    if (isAlreadyTracked) {
      // Untrack
      clearSnapshot(accountId, product.asin);
      setMediaByAsin(prev => {
        const copy = { ...prev };
        delete copy[product.asin];
        return copy;
      });
      setChangesByAsin(prev => {
        const copy = { ...prev };
        delete copy[product.asin];
        return copy;
      });
      return;
    }

    setMediaLoadingAsin(product.asin);
    setError("");
    try {
      const res = await fetchProductMedia(product.asin);
      setMediaByAsin(prev => ({ ...prev, [product.asin]: res.data }));

      // Diff against snapshot
      const snapshot = loadSnapshot(accountId, product.asin);
      if (snapshot) {
        const diff = diffMediaSnapshot(snapshot, res.data);
        if (diff && diff.hasChanges) {
          setChangesByAsin(prev => ({ ...prev, [product.asin]: diff }));
        } else {
          // No changes, remove from alerts
          setChangesByAsin(prev => {
            const copy = { ...prev };
            delete copy[product.asin];
            return copy;
          });
        }
      } else {
        // First save, no alert
        saveSnapshot(accountId, product.asin, res.data);
        setChangesByAsin(prev => {
          const copy = { ...prev };
          delete copy[product.asin];
          return copy;
        });
      }
    } catch (err) {
      console.error('[MediaTracking] SerpAPI fetch failed:', err);
      setError(`Failed to fetch media for ${product.asin}: ${err?.response?.data?.error || err.message}`);
    } finally {
      setMediaLoadingAsin(null);
    }
  };

  const handleRefreshAll = async () => {
    if (!mediaData?.length) return;
    setError("");
    const uniqueAsins = Array.from(new Set(mediaData.map(m => m.asin).filter(Boolean)));
    const toTrack = uniqueAsins.filter(asin => !mediaByAsin[asin]); // only untracked

    if (toTrack.length === 0) {
      setError("All products are already tracked. Untrack some to refresh them individually.");
      return;
    }

    // Track all in parallel with a simple counter
    let successCount = 0;
    let failCount = 0;
    setMediaLoadingAsin('*'); // special marker: refreshing all
    try {
      const promises = toTrack.map(async (asin) => {
        try {
          const res = await fetchProductMedia(asin);
          setMediaByAsin(prev => ({ ...prev, [asin]: res.data }));
          successCount++;
        } catch (err) {
          failCount++;
          console.warn(`[MediaTracking] Failed to track ${asin}:`, err.message);
        }
      });
      await Promise.all(promises);
      if (failCount > 0) {
        setError(`Tracked ${successCount}/${toTrack.length} products. ${failCount} failed.`);
      }
    } finally {
      setMediaLoadingAsin(null);
    }
  };

  const handleMarkReviewed = (asin) => {
    const currentMedia = mediaByAsin[asin];
    if (currentMedia) {
      saveSnapshot(accountId, asin, currentMedia);
    }
    setChangesByAsin(prev => {
      const copy = { ...prev };
      delete copy[asin];
      return copy;
    });
  };

  const handleDismissAllChanges = () => {
    // Save all current media as new baseline
    Object.keys(changesByAsin).forEach(asin => {
      const currentMedia = mediaByAsin[asin];
      if (currentMedia) {
        saveSnapshot(accountId, asin, currentMedia);
      }
    });
    setChangesByAsin({});
  };

  const trackedCount = Object.keys(mediaByAsin).length;

  return (
    <Layout>
      <div className="container-fluid py-4">
        <h2 className="fw-bold mb-4">
          <i className="bi bi-image"></i> Competitor Media Tracking
        </h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-3">
          <div className="col-md-4 d-flex align-items-center">
            <select
              className="form-select"
              value={selectedBasket?.id || ""}
              onChange={(e) =>
                setSelectedBasket(
                  baskets.find((b) => b.id === parseInt(e.target.value)) || null
                )
              }
            >
              <option value="">Select a basket...</option>
              {baskets.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="text-center">
            <div className="spinner-border" />
          </div>
        )}

        {!loading && !selectedBasket && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-image display-4 text-muted"></i>
              <h4 className="mt-3">Competitor Media Tracking</h4>
              <p className="text-muted">Select a basket to view competitor product media</p>
            </div>
          </div>
        )}

        {!loading && selectedBasket && mediaData.length === 0 && !error && (
          <div className="alert alert-warning">
            No competitor product media available for this basket
          </div>
        )}

        {!loading && selectedBasket && mediaData.length > 0 && (() => {
          const uniqueProducts = Array.from(
            mediaData.reduce((map, item) => {
              const existing = map.get(item.asin);
              if (!existing) {
                map.set(item.asin, item);
                return map;
              }
              // Prefer the record that actually has a product photo, then the most recent date.
              const existingHasPhoto = !!existing.product_photo;
              const itemHasPhoto = !!item.product_photo;
              if (itemHasPhoto && !existingHasPhoto) {
                map.set(item.asin, item);
              } else if (itemHasPhoto === existingHasPhoto && (item.date || '') > (existing.date || '')) {
                map.set(item.asin, item);
              }
              return map;
            }, new Map()).values()
          );

          return (
            <>
              {Object.keys(changesByAsin).length > 0 && (
                <div className="alert alert-warning d-flex align-items-center justify-content-between mb-3">
                  <span>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <strong>{Object.keys(changesByAsin).length} competitor{Object.keys(changesByAsin).length > 1 ? 's' : ''}</strong>
                    {' '}updated their content since your last visit.
                  </span>
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={handleDismissAllChanges}
                  >
                    Mark All Reviewed
                  </button>
                </div>
              )}

              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Competitor Products — {selectedBasket.name}</h5>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary position-relative"
                      onClick={() => navigate('/tracking/media-history')}
                    >
                      <i className="bi bi-clock-history me-1"></i>Change History
                      {unreviewedCount > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '10px' }}>
                          {unreviewedCount}
                        </span>
                      )}
                    </button>
                    <span className="badge bg-light text-muted">{uniqueProducts.length} products</span>
                    {trackedCount > 0 && (
                      <span className="badge bg-success">{trackedCount} tracked</span>
                    )}
                    {uniqueProducts.length - trackedCount > 0 && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleRefreshAll}
                        disabled={mediaLoadingAsin === '*'}
                      >
                        {mediaLoadingAsin === '*'
                          ? <><span className="spinner-border spinner-border-sm me-1" /> Tracking all...</>
                          : <><i className="bi bi-arrow-clockwise me-1"></i> Track All ({uniqueProducts.length - trackedCount})</>}
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {uniqueProducts.map((item, idx) => (
                      <div key={item.asin} className="col-md-3 col-sm-6">
                        <ProductCard
                          row={item}
                          media={mediaByAsin[item.asin]}
                          onTrack={handleTrackProduct}
                          isTracked={!!mediaByAsin[item.asin]}
                          isLoadingTrack={mediaLoadingAsin === item.asin}
                          changedFields={changesByAsin[item.asin]?.changedFields || []}
                          onMarkReviewed={handleMarkReviewed}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </Layout>
  );
};

export default MediaTracking;
